import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { DocsLayout, DocsSkeleton } from '~/components/docs';
import { DocsHistoryTimeline } from '~/components/docs/DocsHistoryTimeline';
import { useToast } from '~/components/feedback';
import { fetchDocPage } from '~/lib/api/docs';
import { useAuthStore } from '~/store/auth';

export const Route = createFileRoute('/_auth/docs/$role/$slug/history')({
  component: DocsHistoryRoute,
});

function DocsHistoryRoute(): JSX.Element {
  const params = useParams({ from: '/_auth/docs/$role/$slug/history' });
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role ?? 'viewer');
  const isAdmin = userRole === 'admin';
  const toast = useToast();
  const qc = useQueryClient();

  const pageQuery = useQuery({
    queryKey: ['docs', 'page', params.role, params.slug, 'ru'],
    queryFn: () => fetchDocPage(params.role, params.slug, { locale: 'ru' }),
  });

  return (
    <DocsLayout
      toc={undefined}
      currentRole={params.role}
      currentSlug={params.slug}
      breadcrumbs={[
        { label: 'Документация', href: '/docs' },
        {
          label: pageQuery.data?.title ?? params.slug,
          href: `/docs/${params.role}/${params.slug}`,
        },
        { label: 'История версий' },
      ]}
    >
      {pageQuery.isLoading ? (
        <DocsSkeleton />
      ) : pageQuery.data ? (
        <DocsHistoryTimeline
          role={params.role}
          slug={params.slug}
          currentVersion={pageQuery.data.version}
          canRestore={isAdmin}
          onPreview={(_version) => {
            // На MVP — переход в read view. Preview конкретной ревизии — отдельным sub-route.
            void navigate({
              to: '/docs/$role/$slug',
              params: { role: params.role, slug: params.slug },
            });
          }}
          onRestored={(newVersion) => {
            toast.push({
              variant: 'success',
              title: `Восстановлено как версия ${newVersion}`,
            });
            void qc.invalidateQueries({ queryKey: ['docs'] });
            void navigate({
              to: '/docs/$role/$slug',
              params: { role: params.role, slug: params.slug },
            });
          }}
        />
      ) : null}
    </DocsLayout>
  );
}
